import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Environment } from 'src/environments/environment';
import { HttpService } from './http.service';
import { WooCommerceUserInfoResponse } from '../models/woocommerce-user.model';
import { ProductsResponse, Product } from '../models/woocommerce-product.model';
import {
  Order,
  OrdersListResponse,
  OrdersListParams,
  LineItem,
  CreateOrderRequest
} from '../models/woocommerce-order.model';
import { Shipping } from '../models/woocommerce-customer.model';
import { UpdateCustomerRequest, CustomerResponse } from '../models/woocommerce-customer.model';
import { ProductAtrapaloResponse, ProductAtrapaloParams } from '../models/woocommerce-atrapalo.model';

@Injectable({ providedIn: 'root' })
export class WooCommerceService {
  private readonly base = `${Environment.API_BASE_URL_V2}/api/woocommerce/v1`;
  /** Caché en memoria de user-info para no repetir la petición en cada navegación. Se invalida en logout. */
  private storeUserInfoCache: WooCommerceUserInfoResponse | null = null;

  constructor(
    private httpService: HttpService,
    private http: HttpClient
  ) {}

  /**
   * Obtiene la info de usuario para la tienda (backend ya autenticado por Bearer).
   * Usa caché en memoria: si ya se cargó en esta sesión, devuelve el valor cacheado sin nueva petición.
   */
  getStoreUserInfo(): Observable<WooCommerceUserInfoResponse> {
    if (this.storeUserInfoCache) {
      return of(this.storeUserInfoCache);
    }
    return this.httpService.myPost(`${this.base}/user-info`, {}).pipe(
      tap(res => { this.storeUserInfoCache = res; })
    );
  }

  /** Devuelve la user-info en caché (si existe) para rellenar datos de forma síncrona y evitar parpadeo. */
  getCachedStoreUserInfo(): WooCommerceUserInfoResponse | null {
    return this.storeUserInfoCache;
  }

  /** Extrae el factor de conversión de una respuesta user-info (para uso en componentes). */
  getConversionFactorFromUserInfo(res: any): number {
    if (res?.conversion_factor != null) return Number(res.conversion_factor);
    if (res?.user?.conversion_factor != null) return Number(res.user.conversion_factor);
    return 1;
  }

  /** Extrae el nombre de puntos de una respuesta user-info (para uso en componentes). */
  getPointsNameFromUserInfo(res: any): string {
    return res?.points_name ?? res?.pointsName ?? res?.user?.points_name ?? '';
  }

  /** Invalida la caché de user-info (llamar al cerrar sesión). */
  clearStoreUserInfoCache(): void {
    this.storeUserInfoCache = null;
  }

  /**
   * Login con token de la tienda (por si en el futuro el backend recibe shopToken en el body).
   */
  loginWithShopToken(shopToken: string): Observable<WooCommerceUserInfoResponse> {
    return this.httpService.myPost(`${this.base}/user-info`, { shopToken });
  }

  getProducts(): Observable<ProductsResponse> {
    return this.httpService.myGet(`${this.base}/products`);
  }

  getProductById(id: number): Observable<Product> {
    return this.httpService.myGet(`${this.base}/products/${id}`);
  }

  /**
   * Productos Atrapalo asociados a un producto WooCommerce.
   * Solo válido cuando el producto tiene meta_data atrapa_tu_experiencia = "1".
   */
  getProductAtrapalo(
    productId: number,
    params?: ProductAtrapaloParams
  ): Observable<ProductAtrapaloResponse> {
    const queryParams: Record<string, string> = {};
    if (params?.page != null) queryParams.page = String(params.page);
    if (params?.pageSize != null) queryParams.pageSize = String(params.pageSize);
    return this.httpService.myGet(
      `${this.base}/products/${productId}/atrapalo`,
      Object.keys(queryParams).length ? queryParams : undefined
    );
  }

  getCustomer(): Observable<CustomerResponse> {
    return this.httpService.myGet(`${this.base}/customer`);
  }

  updateCustomer(body: UpdateCustomerRequest): Observable<any> {
    return this.httpService.myPut(`${this.base}/customer`, body);
  }

  createOrder(lineItems: LineItem[], shipping?: Shipping, conversionFactor?: number): Observable<Order> {
    const body: CreateOrderRequest = {
      line_items: lineItems,
      ...(shipping && { shipping, is_physical: true }),
      ...(conversionFactor != null && conversionFactor > 0 && { conversion_factor: conversionFactor })
    };
    return this.httpService.myPost(`${this.base}/create-order`, body);
  }

  getOrders(params?: OrdersListParams): Observable<OrdersListResponse> {
    return this.httpService.myGet(`${this.base}/orders`, params || {});
  }

  getOrderById(id: number): Observable<Order> {
    return this.httpService.myGet(`${this.base}/orders/${id}`);
  }

  updateOrder(id: number, body: any): Observable<Order> {
    return this.httpService.myPut(`${this.base}/orders/${id}`, body);
  }

  cancelOrder(id: number): Observable<any> {
    return this.httpService.myPost(`${this.base}/orders/${id}/cancel`, {});
  }

  getOrderCertificatePdf(orderId: number, licenseId?: number): Observable<Blob> {
    const params = licenseId != null ? { license_id: licenseId.toString() } : {};
    return this.http.get(`${this.base}/orders/${orderId}/certificate-pdf`, {
      params,
      responseType: 'blob'
    });
  }

  getOrderCertificatePdfUrl(orderId: number): string {
    return `${this.base}/orders/${orderId}/certificate-pdf`;
  }

  /** Descarga un archivo desde una URL directa (p. ej. producto descargable de WooCommerce). */
  getFileAsBlob(url: string): Observable<Blob> {
    return this.http.get(url, { responseType: 'blob' });
  }

  resendOrderLicenseEmail(
    orderId: number,
    type: 'combined' | 'single',
    licenseId?: number
  ): Observable<any> {
    const body = type === 'single' && licenseId != null
      ? { type, license_id: licenseId }
      : { type };
    return this.httpService.myPost(`${this.base}/orders/${orderId}/resend-license-email`, body);
  }

  /** Obtiene las categorías (campañas) de la tienda. */
  getCategories(): Observable<{ id: number; name: string }[]> {
    return this.httpService.myGet(`${this.base}/categories`);
  }

  /** Admin: todos los pedidos de la tienda. Requiere permiso Loyalitix store-management. */
  getAdminOrders(params?: OrdersListParams): Observable<OrdersListResponse> {
    return this.httpService.myGet(`${this.base}/admin/orders`, params || {});
  }

  /** Admin: detalle de un pedido. */
  getAdminOrderById(id: number): Observable<Order> {
    return this.httpService.myGet(`${this.base}/admin/orders/${id}`);
  }

  /** Admin: actualizar pedido (estado, billing, shipping, customer_note). */
  updateAdminOrder(
    id: number,
    body: { status?: string; billing?: any; shipping?: any; customer_note?: string }
  ): Observable<Order> {
    return this.httpService.myPut(`${this.base}/admin/orders/${id}`, body);
  }

  /**
   * Admin: exportar pedidos a CSV.
   * GET /admin/orders/export con status, after, before, search, order, orderby (sin page/per_page).
   * Respuesta: archivo CSV (Content-Disposition: attachment), máx. 5000.
   */
  getAdminOrdersExport(params: {
    status?: string;
    after?: string;
    before?: string;
    search?: string;
    order?: string;
    orderby?: string;
    category?: number;
  }): Observable<Blob> {
    const queryParams: Record<string, string> = {};
    if (params?.status?.trim()) queryParams.status = params.status.trim();
    if (params?.after?.trim()) queryParams.after = params.after.trim();
    if (params?.before?.trim()) queryParams.before = params.before.trim();
    if (params?.search?.trim()) queryParams.search = params.search.trim();
    if (params?.order) queryParams.order = params.order;
    if (params?.orderby) queryParams.orderby = params.orderby;
    if (params?.category) queryParams.category = String(params.category);
    return this.http.get(`${this.base}/admin/orders/export`, {
      params: queryParams,
      responseType: 'blob'
    });
  }

}
